import React from 'react';
import {Entity} from '../../../util/uiconstants';
import {isNullOrUndefined, currentResource} from '../../../util/utils';
import {useCanvasFormMetadata, useUserPreferences} from '../../../util/customHooks';
import Utility from '../../../util/utilityfunctions';
import CreateEditAccountModalWrapper from './CreateEditAccountModalWrapper';
import CreateEditRiskModalWrapper from './CreateEditRiskModalWrapper';
import CreateEditMestModalWrapper from './CreateEditMestModalWrapper';
import CreateEditControlModalWrapper from './CreateEditControlModalWrapper';
import CreateEditITProcessModalWrapper from './CreateEditITProcessModalWrapper';
import CreateEditITControlModalWrapper from './CreateEditITControlModalWrapper';
import CreateEditITRiskModalWrapper from './CreateEditITRiskModalWrapper';
import CreateEditITApplicationModalWrapper from './CreateEditITApplicationModalWrapper';
import CreateEditSCOTModalWrapper from './CreateEditSCOTModalWrapper';
import CreateEditEstimateModalWrapper from './CreateEditEstimateModalWrapper';
import CreateEditSampleTagModalWrapper from './CreateEditSampleTagModalWrapper';
import CreateEditTagGroupModalWrapper from './CreateEditTagGroupModalWrapper';

export default function CreateEditEntityModal(props) {
	const {
		engagementId,
		entityType,
		entityId,
		itProcessId,
		itProcessName,
		parentEntityId,
		parentEntityName,
		entitySelector, // Custom selector function when the consumer needs to get the entity from another store slice.
		showCreateEditEntityModal,
		setShowCreateEditEntityModal,
		isManageEntities,
		onEntityCreated,
		onEntityModified,
		parentEntitiesSelector,
		setIsEntityUpdated,
		isITGC, // Optional. Can be infered through itControlDetails.itApproachTypeId
		paginationParamsForServerCall
	} = props;
	const {isShared, isTargetEngagement} = useCanvasFormMetadata();

	const userPreferences = useUserPreferences();

	const isCreate = isNullOrUndefined(entityId);
	const isEdit = !isCreate;

	const theme = Utility.getTheme();

	const renderCreateEditEntityModal = () => {
		switch (entityType) {
			case Entity.Account:
				return (
					<CreateEditAccountModalWrapper
						isManageAccounts={isManageEntities}
						isCreate={isCreate}
						isEdit={isEdit}
						accountId={entityId}
						entitySelector={entitySelector}
						parentEntitiesSelector={parentEntitiesSelector}
						showModal={showCreateEditEntityModal}
						setShowModal={setShowCreateEditEntityModal}
						theme={theme}
						onEntityCreated={onEntityCreated}
						onEntityModified={onEntityModified}
						numberFormat={userPreferences?.numberFormat}
						onEditAccountAction={() => setIsEntityUpdated(true)}
						setIsEntityUpdated={setIsEntityUpdated}
					/>
				);
			case Entity.Risk:
				return (
					<CreateEditRiskModalWrapper
						isManageRisks={isManageEntities}
						isCreate={isCreate}
						isEdit={isEdit}
						riskId={entityId}
						entitySelector={entitySelector}
						parentEntitiesSelector={parentEntitiesSelector}
						showModal={showCreateEditEntityModal}
						setShowModal={setShowCreateEditEntityModal}
						theme={theme}
						onEntityCreated={onEntityCreated}
						onEntityModified={onEntityModified}
						numberFormat={userPreferences?.numberFormat}
					/>
				);
			case Entity.STEntity:
				return (
					<CreateEditMestModalWrapper
						isManageEntities={isManageEntities}
						isCreate={isCreate}
						isEdit={isEdit}
						entityId={entityId}
						entitySelector={entitySelector}
						parentEntitiesSelector={parentEntitiesSelector}
						showModal={showCreateEditEntityModal}
						setShowModal={setShowCreateEditEntityModal}
						theme={theme}
						onEntityCreated={onEntityCreated}
						onEntityModified={onEntityModified}
						numberFormat={userPreferences?.numberFormat}
					/>
				);
			case Entity.Control:
				return (
					<CreateEditControlModalWrapper
						isManageEntities={isManageEntities}
						isCreate={isCreate}
						isEdit={isEdit}
						entityId={entityId}
						entitySelector={entitySelector}
						parentEntitiesSelector={parentEntitiesSelector}
						showModal={showCreateEditEntityModal}
						setShowModal={setShowCreateEditEntityModal}
						viewOnly={isEdit && isShared && isTargetEngagement ? true : false}
						theme={theme}
						onEntityCreated={onEntityCreated}
						onEntityModified={onEntityModified}
						numberFormat={userPreferences?.numberFormat}
					/>
				);
			case Entity.ITProcess:
				return (
					<CreateEditITProcessModalWrapper
						isCreate={isCreate}
						isEdit={isEdit}
						entityId={entityId}
						entitySelector={entitySelector}
						parentEntitiesSelector={parentEntitiesSelector}
						showModal={showCreateEditEntityModal}
						setShowModal={setShowCreateEditEntityModal}
						theme={theme}
						onEntityCreated={onEntityCreated}
						onEntityModified={onEntityModified}
						numberFormat={userPreferences?.numberFormat}
					/>
				);
			case Entity.ITControl:
				return (
					<CreateEditITControlModalWrapper
						isITGC={isITGC}
						isManageEntities={isManageEntities}
						isCreate={isCreate}
						isEdit={isEdit}
						entityId={entityId}
						itProcessId={itProcessId}
						itProcessName={itProcessName}
						parentEntityId={parentEntityId}
						parentEntityName={parentEntityName}
						entitySelector={entitySelector}
						parentEntitiesSelector={parentEntitiesSelector}
						showModal={showCreateEditEntityModal}
						setShowModal={setShowCreateEditEntityModal}
						entityType={entityType}
						theme={theme}
						onEntityCreated={onEntityCreated}
						onEntityModified={onEntityModified}
						numberFormat={userPreferences?.numberFormat}
					/>
				);
			case Entity.ITRisk:
				return (
					<CreateEditITRiskModalWrapper
						isManageEntities={isManageEntities}
						isCreate={isCreate}
						isEdit={isEdit}
						entityId={entityId}
						entitySelector={entitySelector}
						parentEntitiesSelector={parentEntitiesSelector}
						showModal={showCreateEditEntityModal}
						setShowModal={setShowCreateEditEntityModal}
						theme={theme}
						onEntityCreated={onEntityCreated}
						onEntityModified={onEntityModified}
						numberFormat={userPreferences?.numberFormat}
					/>
				);
			case Entity.ITSOApplication:
				return (
					<CreateEditITApplicationModalWrapper
						isManageEntities={isManageEntities}
						isCreate={isCreate}
						isEdit={isEdit}
						entityId={entityId}
						entitySelector={entitySelector}
						parentEntitiesSelector={parentEntitiesSelector}
						showModal={showCreateEditEntityModal}
						setShowModal={setShowCreateEditEntityModal}
						theme={theme}
						onEntityCreated={onEntityCreated}
						onEntityModified={onEntityModified}
						numberFormat={userPreferences?.numberFormat}
					/>
				);
			case Entity.SCOT:
				return (
					<CreateEditSCOTModalWrapper
						isCreate={isCreate}
						isEdit={isEdit}
						entityId={entityId}
						entitySelector={entitySelector}
						parentEntitiesSelector={parentEntitiesSelector}
						showModal={showCreateEditEntityModal}
						setShowModal={setShowCreateEditEntityModal}
						theme={theme}
						onEntityCreated={onEntityCreated}
						onEntityModified={onEntityModified}
						numberFormat={userPreferences?.numberFormat}
					/>
				);
			case Entity.Estimate:
				return (
					<CreateEditEstimateModalWrapper
						isManageEntities={isManageEntities}
						isCreate={isCreate}
						isEdit={isEdit}
						estimateId={entityId}
						entitySelector={entitySelector}
						parentEntitiesSelector={parentEntitiesSelector}
						showModal={showCreateEditEntityModal}
						setShowModal={setShowCreateEditEntityModal}
						theme={theme}
						onEntityCreated={onEntityCreated}
						onEntityModified={onEntityModified}
						numberFormat={userPreferences?.numberFormat}
					/>
				);
			case Entity.SampleItem:
				return (
					<CreateEditSampleTagModalWrapper
						engagementId={engagementId}
						isCreate={isCreate}
						showModal={showCreateEditEntityModal}
						setShowModal={setShowCreateEditEntityModal}
						labels={currentResource.createTagLabels}
						onEntityCreated={onEntityCreated}
						entityId={entityId}
						paginationParamsForServerCall={paginationParamsForServerCall}
						{...props}
					/>
				);
			case Entity.SampleTagGroups:
				return (
					<CreateEditTagGroupModalWrapper
						showModal={showCreateEditEntityModal}
						setShowModal={setShowCreateEditEntityModal}
						onClose={() => {
							setShowCreateEditEntityModal(false);
							onEntityCreated();
						}}
						entityId={entityId}
						isCreate={isCreate}
						paginationParamsForServerCall={paginationParamsForServerCall}
					/>
				);
			default:
				return null;
		}
	};

	const createEditModalRender = renderCreateEditEntityModal();

	return <React.Fragment>{showCreateEditEntityModal && createEditModalRender}</React.Fragment>;
}
